/**
 * EMAIL_REGEX - Detects email addresses in text
 *
 * MATCHES:
 * - Standard emails: user@example.com, test.email@domain.org
 * - Emails with numbers: user123@test.com, test@domain123.net
 * - Emails with special chars: user.name@example.com, user+tag@domain.co.uk
 * - Subdomains: user@mail.example.com, test@subdomain.domain.org
 *
 * DOES NOT MATCH:
 * - Invalid formats: @domain.com, user@, user@domain (no TLD)
 * - Emails without proper TLD: user@domain.c (TLD must be 2+ chars)
 * - Partial matches within words: emailuser@domain.com within "myemailuser@domain.com"
 */
const EMAIL_REGEX = /\b[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Z|a-z]{2,}\b/g;

/**
 * PHONE_REGEX - Detects US phone numbers in various formats
 *
 * MATCHES:
 * - Standard format: (555) 123-4567, 555-123-4567
 * - Dot separated: 555.123.4567
 * - Space separated: 555 123 4567
 * - No separators: 5551234567
 * - With country code: +1-555-123-4567, 1-555-123-4567
 * - Mixed separators: (555) 123.4567, 555-123 4567
 *
 * DOES NOT MATCH:
 * - Non-US formats: +44 20 1234 5678, +33 1 23 45 67 89
 * - Invalid lengths: 555-123-456 (too short), 555-123-45678 (too long)
 * - Letters in number: 555-ABC-4567, CALL-NOW
 * - Incomplete: 555-123, (555)
 * - Numbers not at word boundaries: within larger numbers like 15551234567890
 * - Long numeric strings: 12354192835412938414545 (false positive protection)
 */
const PHONE_REGEX = /(?<!\d)(?:\+?1[-.\s]?)?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})\b(?!\d)/g;

/**
 * URL_REGEX - Matches common URL patterns to filter them out before contact detection
 *
 * MATCHES:
 * - http/https URLs: https://example.com, http://domain.org/path
 * - URLs with query params: https://site.com?param=123&id=456
 * - URLs with paths: https://example.com/path/to/page
 * - URLs with ports: https://example.com:8080/api
 * - www URLs without protocol: www.example.com
 *
 * DOES NOT MATCH:
 * - Email addresses: john@example.com (ensured by requiring protocol or www)
 *
 * This prevents false positives from numbers in URLs being detected as phone numbers
 * while preserving the ability to detect email addresses
 */
const URL_REGEX =
  /(?:https?:\/\/|www\.)[-a-zA-Z0-9@:%._+~#=]{1,256}\.[a-zA-Z0-9()]{1,6}\b(?:[-a-zA-Z0-9()@:%_+.~#?&/=]*)/gi;

/**
 * SSN_REGEX - Detects US Social Security Numbers
 *
 * MATCHES:
 * - Standard dashed format: 123-45-6789
 *
 * DOES NOT MATCH:
 * - Undashed 9-digit strings: 123456789 (avoids false positives on order/ID numbers)
 * - Phone numbers: 555-123-4567 (different digit grouping)
 * - Numbers embedded inside longer digit sequences
 *
 * Intentionally conservative — only matches the canonical XXX-XX-XXXX dashed
 * format to minimize false positives. Additional patterns may be added later.
 */
const SSN_REGEX = /\b\d{3}-\d{2}-\d{4}\b/g;

/**
 * Normalizes text by replacing common contact info obfuscation patterns
 * with their standard equivalents to enable detection
 *
 * HANDLES:
 * - Removes URLs to prevent false positives from numeric query parameters
 * - Email @ obfuscation: [at], (at), {at}, <at>, AT (with spaces)
 * - Email dot obfuscation: [dot], (dot), {dot}, DOT (with spaces)
 * - Extra spacing: Removes excessive spaces in potential contact patterns
 *
 * EXAMPLES:
 * - "me[at]domain.com" → "me@domain.com"
 * - "me @ domain.com" → "me@domain.com"
 * - "domain[dot]com" → "domain.com"
 * - "1 - 800 - 444 - 5678" → "1-800-444-5678"
 * - "https://example.com/?id=1234567890" → (URL removed before processing)
 *
 * @param text - The text to normalize
 * @returns Normalized text with URLs removed and obfuscation patterns replaced
 */
const normalizeContactObfuscation = (text: string): string => {
  // First, remove URLs to prevent false positives from numbers in URLs
  let normalized = text.replace(URL_REGEX, ' ');

  // Replace [at], (at), {at}, <at> with @
  normalized = normalized.replace(/\s*[[({<]\s*at\s*[\])}>]\s*/gi, '@');

  // Replace " AT " with @ (word boundaries to avoid matching "THAT", "CAT", etc.)
  normalized = normalized.replace(/\b\s*AT\s*\b/g, '@');

  // Replace [dot], (dot), {dot} with .
  normalized = normalized.replace(/\s*[[({<]\s*dot\s*[\])}>]\s*/gi, '.');

  // Replace " DOT " with . (word boundaries)
  normalized = normalized.replace(/\b\s*DOT\s*\b/g, '.');

  // Normalize excessive spacing around @ symbol
  normalized = normalized.replace(/\s*@\s*/g, '@');

  // Normalize phone number spacing patterns (e.g., "1 - 800 - 444 - 5678" → "1-800-444-5678")
  // Replace patterns like " - " or "  -  " with "-"
  normalized = normalized.replace(/\s*-\s*/g, '-');

  // Normalize multiple spaces in sequences that might be phone numbers
  // This helps catch patterns like "555  123  4567"
  normalized = normalized.replace(/(\d)\s{2,}(\d)/g, '$1 $2');

  return normalized;
};

/**
 * Minimal normalization for SSN detection.
 *
 * Intentionally does NOT strip URLs -- unlike email/phone, an SSN embedded
 * inside a URL (e.g. a scam "identity verification" link with
 * `?ssn=123-45-6789`) is still a real disclosure we want to warn the user
 * about. Phone-number false positives from URL query strings are still
 * filtered because phone/email detection continues to run against the
 * fully-normalized text (see {@link normalizeContactObfuscation}).
 *
 * HANDLES:
 * - Collapses whitespace around dashes so spaced forms like
 *   "123 - 45 - 6789" still match `SSN_REGEX`.
 *
 * @param text - The text to normalize for SSN detection
 * @returns Text with spaces around dashes collapsed; URLs preserved
 */
const normalizeForSSN = (text: string): string => {
  return text.replace(/\s*-\s*/g, '-');
};

/**
 * Detects personal contact information (emails, phone numbers, and SSNs) in text
 *
 * This function uses preprocessing to detect obfuscated contact information
 * that bad actors commonly use to bypass automated detection, including:
 * - Emails with spaces: "me @ domain.com"
 * - Obfuscated @ symbol: "me[at]domain.com", "me(at)domain.com"
 * - Obfuscated dots: "domain[dot]com", "domain DOT com"
 * - Phone numbers with extra spacing: "1 - 800 - 444 - 5678"
 * - SSNs with extra spacing around dashes: "123 - 45 - 6789"
 *
 * Email and phone detection run against fully-normalized text (URLs stripped
 * to prevent query-string false positives). SSN detection runs against a
 * lighter-normalized variant that preserves URLs, because an SSN inside a
 * URL is still a real disclosure that warrants a warning.
 *
 * @param text - The text to analyze for contact information
 * @returns Object containing arrays of detected emails, phone numbers, and SSNs
 */
export const detectContactInfo = (
  text: string,
): { emails: Array<string>; phoneNumbers: Array<string>; ssns: Array<string> } => {
  // Email/phone need URL stripping to avoid query-string false positives.
  const normalizedForEmailPhone = normalizeContactObfuscation(text);
  // SSN keeps URLs so SSNs embedded in URLs are still detected.
  const normalizedForSSN = normalizeForSSN(text);

  const emails = Array.from(new Set(normalizedForEmailPhone.match(EMAIL_REGEX) || []));
  const phoneNumbers = Array.from(new Set(normalizedForEmailPhone.match(PHONE_REGEX) || []));
  const ssns = Array.from(new Set(normalizedForSSN.match(SSN_REGEX) || []));

  return {
    emails,
    phoneNumbers,
    ssns,
  };
};

/**
 * Checks if text contains any personal contact details (emails or phone numbers)
 *
 * @param text - The text to check for contact information
 * @returns true if any contact details are found, false otherwise
 */
export const hasPersonalContactDetails = (text: string): boolean => {
  const { emails, phoneNumbers } = detectContactInfo(text);
  return emails.length > 0 || phoneNumbers.length > 0;
};

/**
 * Checks if text contains a US Social Security Number
 *
 * Kept separate from {@link hasPersonalContactDetails} so consumers can show
 * SSN-specific warning copy (per RCRM-5967) instead of the generic
 * email/phone warning.
 *
 * @param text - The text to check for an SSN
 * @returns true if an SSN-like pattern is found, false otherwise
 */
export const hasSSN = (text: string): boolean => {
  const { ssns } = detectContactInfo(text);
  return ssns.length > 0;
};
